import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import {
  ClassicEditor,
  Autoformat,
  Bold,
  Italic,
  Underline,
  BlockQuote,
  CKFinder,
  CKFinderUploadAdapter,
  CloudServices,
  Essentials,
  Heading,
  Image,
  ImageCaption,
  ImageResize,
  ImageStyle,
  ImageToolbar,
  ImageUpload,
  PictureEditing,
  Indent,
  IndentBlock,
  Link,
  List,
  MediaEmbed,
  Mention,
  Paragraph,
  PasteFromOffice,
  Table,
  TableColumnResize,
  TableToolbar,
  TextTransformation,
  Strikethrough,
  Alignment,
  HorizontalLine,
  ImageInsert,
  AutoMediaEmbed,
  Font,
  FontSize,
  FindAndReplace,
  RemoveFormat,
} from 'ckeditor5';
import { Box, FormHelperText } from '@mui/material';
import ImageUploaderPlugin from './Plugins/ImageUploaderPlugin';

const plugins = [
  Font,
  FontSize,
  Autoformat,
  BlockQuote,
  Bold,
  CKFinder,
  CKFinderUploadAdapter,
  CloudServices,
  Essentials,
  Heading,
  Image,
  ImageCaption,
  ImageResize,
  ImageStyle,
  ImageToolbar,
  ImageUpload,
  Indent,
  IndentBlock,
  Italic,
  Link,
  List,
  MediaEmbed,
  Mention,
  Paragraph,
  PasteFromOffice,
  PictureEditing,
  Table,
  TableColumnResize,
  TableToolbar,
  TextTransformation,
  Underline,
  Strikethrough,
  Alignment,
  HorizontalLine,
  MediaEmbed,
  ImageInsert,
  AutoMediaEmbed,
  FindAndReplace,
  RemoveFormat,
];

const AppCKEditor = ({
  data,
  handleChange,
  handleInit,
  handleBlur,
  error,
  helperText,
  imageUploader,
}) => {
  const customPlugins = useMemo(() =>
    imageUploader ? [...plugins, ImageUploaderPlugin] : plugins,
  );

  return (
    <Box
      sx={{
        '& .ck-editor': {
          border: error ? '1px solid red !important' : '0',
        },
        '& .ck-content': {
          padding: '0 2rem !important',
        },
      }}
    >
      <CKEditor
        editor={ClassicEditor}
        config={{
          plugins: customPlugins,
          toolbar: [
            'undo',
            'redo',
            '|',
            'findAndReplace',
            '|',
            'heading',
            '|',
            'fontFamily',
            'fontSize',
            'fontColor',
            'fontBackgroundColor',
            'bold',
            'italic',
            'underline',
            'strikethrough',
            '|',
            'removeFormat',
            '|',
            'link',
            'uploadImage',
            'insertImageViaUrl',
            'insertTable',
            'blockQuote',
            '|',
            'bulletedList',
            'numberedList',
            '|',
            'outdent',
            'indent',
            'alignment',
            'horizontalLine',
            'mediaEmbed',
          ],
          findAndReplace: {
            uiType: 'dropdown',
          },
          fontSize: {
            options: [8, 10, 12, 14, 'default', 18, 20, 24, 36],
          },
          fontFamily: {
            options: [
              'default',
              'Arial, Helvetica, sans-serif',
              'Courier New, Courier, monospace',
              'Georgia, serif',
              'Times New Roman, Times, serif',
              'Tahoma, Geneva, sans-serif',
              'Verdana, Geneva, sans-serif',
              'Comic Sans MS, cursive, sans-serif',
              'Roboto, sans-serif',
              'Lora, serif',
              'Open Sans, sans-serif',
              'Montserrat, sans-serif',
            ],
          },
          heading: {
            options: [
              {
                model: 'paragraph',
                title: 'Paragraph',
                class: 'ck-heading_paragraph',
              },
              {
                model: 'heading1',
                view: 'h1',
                title: 'Heading 1',
                class: 'ck-heading_heading1',
              },
              {
                model: 'heading2',
                view: 'h2',
                title: 'Heading 2',
                class: 'ck-heading_heading2',
              },
              {
                model: 'heading3',
                view: 'h3',
                title: 'Heading 3',
                class: 'ck-heading_heading3',
              },
              {
                model: 'heading4',
                view: 'h4',
                title: 'Heading 4',
                class: 'ck-heading_heading4',
              },
            ],
          },
          image: {
            resizeOptions: [
              {
                name: 'resizeImage:original',
                label: 'Default image width',
                value: null,
              },
              {
                name: 'resizeImage:50',
                label: '50% page width',
                value: '50',
              },
              {
                name: 'resizeImage:75',
                label: '75% page width',
                value: '75',
              },
            ],
            toolbar: [
              'imageTextAlternative',
              'toggleImageCaption',
              '|',
              'imageStyle:inline',
              'imageStyle:wrapText',
              'imageStyle:breakText',
              '|',
              'resizeImage',
            ],
          },
          link: {
            addTargetToExternalLinks: true,
            defaultProtocol: 'https://',
          },
          imageUploader: imageUploader,
          table: {
            contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells'],
          },
          mediaEmbed: {
            previewsInData: true,
            providers: [
              {
                name: 'marketplace',
                url: [
                  /^cdnmarket\.joblogic\.com/,
                  /^jllivemarketplacepub\.blob\.core\.windows\.net/,
                ],
                html: (match) => {
                  return `<video controls width="100%" style="max-height: 500px;">
                      <source src="https://${match.input}#t=1.6" type="video/mp4" />
                      <source src="https://${match.input}#t=1.6" type="video/webm" />
                      <source src="https://${match.input}#t=1.6" type="video/ogg" />
                      <source src="https://${match.input}#t=1.6" type="video/av" />
                      <source src="https://${match.input}#t=1.6" type="video/mov" />
                      <source src="https://${match.input}#t=1.6" type="video/flv" />
                      Your browser does not support the video tag.
                  </video>`;
                },
              },
            ],
          },
        }}
        data={data}
        onChange={handleChange}
        onBlur={handleBlur}
        onInit={handleInit}
      />

      {!!error && (
        <FormHelperText sx={{ color: 'error.main', ml: 2 }}>
          {helperText}
        </FormHelperText>
      )}
    </Box>
  );
};

export default React.memo(AppCKEditor);

AppCKEditor.propTypes = {};
